import React from "react";
import { Callout, DirectionalHint, mergeStyleSets } from "@fluentui/react";
import { Check } from "@phosphor-icons/react";
import { boroondaraPalette } from "../../theme/boroondaraTheme";
import { getPhosphorIcon } from "../../utils/iconMap";
import type { Agent } from "../../types/agent.types";

interface Props {
  agents: Agent[];
  selectedAgent: Agent | null;
  target: HTMLElement | null;
  onSelect: (agent: Agent) => void;
  onDismiss: () => void;
}

const styles = mergeStyleSets({
  item: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    cursor: "pointer",
    borderBottom: "1px solid #EDEDED",
    transition: "background 0.15s",
    selectors: {
      ":hover": {
        background: "#F5F5F5",
      },
      ":last-child": {
        borderBottom: "none",
      },
    },
  },
  itemSelected: {
    background: "#d9f4d7",
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontWeight: 600,
    fontSize: 13,
    color: boroondaraPalette.text,
  },
  desc: {
    fontSize: 11,
    color: boroondaraPalette.text3,
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  check: {
    fontSize: 14,
    color: "#2e873d",
  },
  header: {
    padding: "10px 16px 6px",
    fontSize: 11,
    fontWeight: 600,
    color: "#747474",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 6px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    lineHeight: "12px",
    whiteSpace: "nowrap",
    marginLeft: 6,
  } as any,
});

export default function AgentPicker({
  agents,
  selectedAgent,
  target,
  onSelect,
  onDismiss,
}: Props) {
  if (!target) return null;

  return (
    <Callout
      target={target}
      isBeakVisible={false}
      directionalHint={DirectionalHint.topLeftEdge}
      onDismiss={onDismiss}
      calloutWidth={320}
      styles={{
        root: {
          background: "#FFFFFF",
          border: "1px solid #E1E1E1",
          borderRadius: 4,
          overflow: "hidden",
        },
      }}
    >
      <div className={styles.header}>Select Agent</div>
      {agents.map((a) => {
        const color = boroondaraPalette.primary;
        const IconComp = getPhosphorIcon(a.icon || "Robot");
        const isSelected = selectedAgent?.id === a.id;
        const getStatusMeta = (status: string, tier?: string) => {
          const s = (status || "").toLowerCase();
          const t = tier === "governed" ? "Governed" : "Public";
          if (s === "active") return { label: `${t} \u00b7 Live`, dot: t === "Public" ? "#107C10" : "#D97706", bg: t === "Public" ? "#E8F5E9" : "#FFF8E1", border: t === "Public" ? "#C8E6C9" : "#FFE082" };
          if (s === "coming_soon") return { label: `${t} \u00b7 Coming Soon`, dot: "#F59E0B", bg: "#FFF8E1", border: "#FFE082" };
          if (s === "maintenance") return { label: "Maintenance", dot: "#C8C8C8", bg: "#F5F5F5", border: "#E0E0E0" };
          return null;
        };
        const sm = getStatusMeta(a.status, a.tier);

        return (
          <div
            key={a.id}
            className={`${styles.item} ${isSelected ? styles.itemSelected : ""}`}
            onClick={() => {
              onSelect(a);
              onDismiss();
            }}
          >
            <div
              className={styles.iconWrap}
              style={{
                background: `${color}20`,
                border: `1px solid ${color}40`,
              }}
            >
              <IconComp size={16} color={color} weight="regular" />
            </div>
            <div className={styles.info}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div className={styles.name}>{a.name}</div>
                {sm && (
                  <span className={styles.statusBadge} style={{ background: sm.bg, border: `1px solid ${sm.border}`, color: "#484848" }}>
                    <span style={{ width: 5, height: 5, borderRadius: 999, background: sm.dot }} />
                    {sm.label}
                  </span>
                )}
              </div>
              <div className={styles.desc}>{a.description}</div>
            </div>
            {isSelected && (
              <Check size={14} color="#2e873d" weight="bold" />
            )}
          </div>
        );
      })}
    </Callout>
  );
}
